import { useState } from 'react';
import { motion } from 'framer-motion';
import { Bot, Plus, Users, Calendar, Bell, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/i18n/LanguageContext';
import { RecipientList } from './RecipientList';
import { AddRecipientDialog } from './AddRecipientDialog';
import { PendingApprovals } from './PendingApprovals';

export function AutoComposeSettings() {
  const { language } = useLanguage();
  const [isEnabled, setIsEnabled] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'recipients' | 'pending'>('recipients');

  const content = {
    en: {
      title: 'AI Auto-Compose',
      description: 'Let AI create and send heartfelt announcements on your behalf',
      enableLabel: 'Enable Auto-Compose',
      enableDescription: 'AI will generate personalized messages for your recipients on schedule',
      addRecipient: 'Add Recipient',
      tabs: {
        recipients: 'Recipients',
        pending: 'Pending Approval',
      },
      features: [
        { icon: Bot, text: 'AI crafts personalized messages' },
        { icon: Calendar, text: 'Set your preferred schedule' },
        { icon: Bell, text: '24-hour preview before sending' },
      ],
    },
    es: {
      title: 'IA Auto-Componer',
      description: 'Deja que la IA cree y envíe anuncios sinceros en tu nombre',
      enableLabel: 'Activar Auto-Componer',
      enableDescription: 'La IA generará mensajes personalizados para tus destinatarios según el horario',
      addRecipient: 'Agregar Destinatario',
      tabs: {
        recipients: 'Destinatarios',
        pending: 'Pendiente de Aprobación',
      },
      features: [
        { icon: Bot, text: 'La IA crea mensajes personalizados' },
        { icon: Calendar, text: 'Establece tu horario preferido' },
        { icon: Bell, text: 'Vista previa 24 horas antes de enviar' },
      ],
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">{t.title}</CardTitle>
                <CardDescription>{t.description}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{t.enableLabel}</span>
              <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {t.features.map((feature, index) => (
              <motion.div
                key={index}
                className="flex items-center gap-3 p-3 rounded-lg bg-background/50"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <feature.icon className="h-5 w-5 text-primary" />
                <span className="text-sm">{feature.text}</span>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('recipients')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'recipients'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users className="h-4 w-4" />
          {t.tabs.recipients}
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'pending'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Bell className="h-4 w-4" />
          {t.tabs.pending}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'recipients' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAddDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              {t.addRecipient}
            </Button>
          </div>
          <RecipientList isEnabled={isEnabled} />
        </div>
      )}

      {activeTab === 'pending' && <PendingApprovals />}

      <AddRecipientDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
    </div>
  );
}
